import { useState, useEffect, useRef, useCallback } from 'react';

interface PollingOptions {
  interval?: number; // 轮询间隔，默认1000ms
  maxAttempts?: number; // 最大尝试次数，默认不限制
  onSuccess?: (data: any) => void; // 成功回调
  onError?: (error: any) => void; // 错误回调
}

interface PollingResult<T> {
  data: T | null;
  loading: boolean;
  error: any;
  startPolling: (params?: any) => void;
  stopPolling: () => void;
  isPolling: boolean;
}

/**
 * 轮询 API 的 hooks
 * @param apiCall 要轮询的 API 函数
 * @param checkCondition 检查是否继续轮询的条件函数，返回 true 表示继续轮询，false 表示停止
 * @param options 配置选项
 */
export function usePollingApi<T = any>(
  apiCall: (params?: any) => Promise<any>,
  checkCondition: (response: any) => boolean,
  options: PollingOptions = {}
): PollingResult<T> {
  const { interval = 3000, maxAttempts, onSuccess, onError } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);

  const timerRef = useRef<number | null>(null);
  const attemptCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const paramsRef = useRef<any>(null);

  // 清理定时器
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 停止轮询
  const stopPolling = useCallback(() => {
    clearTimer();
    setIsPolling(false);
  }, [clearTimer]);

  // 执行 API 调用
  const executeApiCall = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCall(paramsRef.current);

      // 检查是否应该继续轮询
      const shouldContinue = checkCondition(response);

      if (!shouldContinue) {
        // 停止轮询，设置数据
        setData(response);
        setIsPolling(false);
        clearTimer();
        onSuccess?.(response);
      } else {
        // 检查是否达到最大尝试次数
        if (maxAttempts && attemptCountRef.current >= maxAttempts) {
          setError(new Error('达到最大尝试次数'));
          setIsPolling(false);
          clearTimer();
          onError?.(new Error('达到最大尝试次数'));
          return;
        }

        // 继续轮询
        attemptCountRef.current++;
      }
    } catch (err) {
      setError(err);
      setIsPolling(false);
      clearTimer();
      onError?.(err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiCall, checkCondition, maxAttempts, onSuccess, onError, clearTimer]);

  // 开始轮询
  const startPolling = useCallback(
    (params?: any) => {
      if (isPolling) return;

      // 保存参数
      paramsRef.current = params;
      setIsPolling(true);
      attemptCountRef.current = 0;

      // 立即执行一次
      executeApiCall();

      // 设置定时器
      timerRef.current = setInterval(executeApiCall, interval);
    },
    [isPolling, executeApiCall, interval]
  );

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  return {
    data,
    loading,
    error,
    startPolling,
    stopPolling,
    isPolling
  };
}
